import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import shipperProfessional from '@/assets/shipper-professional.jpg';
import LRDirectBoardMockup from '@/components/marketing/mockups/LRDirectBoardMockup';
import LoadBoardMockup from '@/components/marketing/mockups/LoadBoardMockup';
import TrustScoreMockup from '@/components/marketing/mockups/TrustScoreMockup';
import AtlasInsightsMockup from '@/components/marketing/mockups/AtlasInsightsMockup';
import {
  ArrowRight, Check, Sparkles, Network, Handshake, Shield, Gift,
  Mic, Upload, Brain, MapPin, BarChart3,
} from 'lucide-react';

const TEAL = '#02857E';

const ShippersPage: React.FC = () => {
  usePageMeta({
    fullTitle: true,
    title: 'For Shippers | LaneRunner',
    description:
      'Post freight to verified brokers and carriers, or build a private broker network through LaneRunner. Free for shippers. No margin taken on your freight.',
  });

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader activePage="shippers" />

      {/* ============ SECTION 1: HERO ============ */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-br from-[#f0f7f6] via-white to-[#f5faf9] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: `${TEAL}1A` }} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-5 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#02857E]/30 text-[#02857E] text-xs font-semibold tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> FOR SHIPPERS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5">
              Move freight faster.
              <br />
              <span style={{ color: TEAL }}>Skip the gatekeepers.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
              LaneRunner is the modern freight platform for shippers. Post once. Get verified brokers
              and carriers competing for your freight. Or build a private network of brokers you trust
              through LaneRunner.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button
                asChild
                size="lg"
                className="rounded-full text-white font-semibold px-7 shadow-lg hover:opacity-90"
                style={{ backgroundColor: TEAL }}
              >
                <Link to="/auth?role=shipper">
                  Sign Up Free <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> Free for shippers</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> FMCSA-verified network</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> No margin on your freight</span>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <img src={shipperProfessional} alt="Shipping manager reviewing freight" className="w-full h-auto object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 2: WHY LANERUNNER ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why shippers choose LaneRunner</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Direct relationships, open coverage, and free for shippers.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Network,
                title: 'Direct broker relationships',
                body: 'Build your private network through LaneRunner. Invite brokers you trust. Post freight to specific networks. Direct relationships, no margin taken.',
              },
              {
                icon: Handshake,
                title: 'Open marketplace too',
                body: 'Need broader coverage? Post to the open marketplace and get verified brokers competing for your freight.',
              },
              {
                icon: Gift,
                title: 'Free for shippers',
                body: 'We charge brokers for access, not shippers for posting. Free signup, free posting, free LaneRunner features.',
              },
            ].map((c) => (
              <div key={c.title} className="p-6 rounded-2xl border border-gray-200 hover:border-[#02857E]/40 hover:shadow-lg transition-all bg-white">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${TEAL}15` }}>
                  <c.icon className="w-6 h-6" style={{ color: TEAL }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 3: PRIVATE BROKER NETWORKS ============ */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden" style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #026d67 100%)` }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold tracking-wider mb-4">
              <Network className="w-3.5 h-3.5" /> PRIVATE BROKER NETWORKS
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Build your private broker network
            </h2>
            <p className="text-lg text-white/90 mb-6 leading-relaxed">
              Invite brokers you trust. Create multiple networks by region or specialty. Post freight
              directly to specific brokers. Stay in control of who sees your loads.
            </p>
            <ul className="space-y-2.5 mb-7">
              {[
                'Create multiple named networks',
                'Invite vetted brokers by email',
                'Post privately, open market, or both',
                'Direct shipper-to-broker relationships',
                'No margin taken on your freight',
                'Free for shippers',
              ].map((t) => (
                <li key={t} className="flex gap-2 text-white"><Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-lr-yellow" />{t}</li>
              ))}
            </ul>
          </div>

          <div>
            <LRDirectBoardMockup />
          </div>
        </div>
      </section>

      {/* ============ SECTION 4: POST FREIGHT MULTIPLE WAYS ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <LoadBoardMockup />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <MapPin className="w-3.5 h-3.5" /> POST FREIGHT
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Post once, reach everyone — or just the brokers you choose
            </h2>

            <div className="space-y-5">
              <div className="p-5 rounded-xl border border-gray-200 bg-gray-50">
                <h3 className="font-bold text-gray-900 mb-1.5">1. Open Marketplace</h3>
                <p className="text-gray-700 leading-relaxed text-sm">
                  Post to the open LaneRunner marketplace. Vetted brokers see your load. Compare quotes.
                  Award the best fit.
                </p>
              </div>
              <div className="p-5 rounded-xl border-2 border-[#02857E]/30 bg-white">
                <h3 className="font-bold text-gray-900 mb-1.5">2. Private Network</h3>
                <p className="text-gray-700 leading-relaxed text-sm">
                  Post to a specific private network you've built. Only invited brokers see your freight.
                  Tighter competition, direct relationships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 5: VERIFIED NETWORK ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Shield className="w-3.5 h-3.5" /> VERIFIED NETWORK
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Every broker and carrier is verified
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              FMCSA verification, document review, manual approval. You see who you're working with
              before you commit.
            </p>
            <ul className="space-y-2.5">
              {[
                'FMCSA verification on every motor carrier',
                'Document review for every shipper',
                'Broker credentials checked',
                'Trust signals visible before you commit',
              ].map((t) => (
                <li key={t} className="flex gap-2 text-gray-800"><Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}</li>
              ))}
            </ul>
          </div>
          <div className="order-1 lg:order-2">
            <TrustScoreMockup />
          </div>
        </div>
      </section>

      {/* ============ SECTION 6: ATLAS AI FOR SHIPPERS ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <AtlasInsightsMockup />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Brain className="w-3.5 h-3.5" /> POWERED BY ATLAS AI
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Smart posting tools, built in</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              ATLAS AI helps shippers post faster and price smarter. Available to every shipper account.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Mic, title: 'Voice Post', body: 'Speak the load — ATLAS parses to fields.' },
                { icon: Upload, title: 'Bulk Upload', body: 'Drop a spreadsheet, post up to 100 loads.' },
                { icon: MapPin, title: 'Multi-stop Loads', body: 'Up to 5 picks and 5 drops per load.' },
                { icon: BarChart3, title: 'Rate Insights', body: 'AI-driven rate intelligence on every lane.' },
              ].map((f) => (
                <div key={f.title} className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${TEAL}15` }}>
                    <f.icon className="w-5 h-5" style={{ color: TEAL }} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-0.5">{f.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 7: PRICING ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Free for shippers</h2>
            <p className="text-lg text-gray-600">No subscription. No catch.</p>
          </div>

          <div className="rounded-2xl border-2 border-[#02857E]/40 p-8 bg-white shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-1">Free Access</h3>
            <p className="text-sm text-gray-600 mb-4">Free for shippers</p>
            <div className="mb-6">
              <span className="text-5xl font-bold text-gray-900">$0</span>
              <span className="text-gray-600">/month</span>
            </div>
            <ul className="space-y-2.5 mb-7 text-sm text-gray-700">
              {[
                'Unlimited load posting',
                'Open marketplace + private networks',
                'Private broker network creation',
                'Multi-stop loads (5 picks / 5 drops)',
                'AI Voice Post & Bulk Upload',
                'Direct messaging with brokers and carriers',
                'ATLAS AI rate insights',
                'Company document storage',
                'Priority support',
              ].map((t) => (
                <li key={t} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}</li>
              ))}
            </ul>
            <Button asChild size="lg" className="w-full rounded-full text-white font-semibold" style={{ backgroundColor: TEAL }}>
              <Link to="/auth?role=shipper">Sign Up Free <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <p className="text-xs text-gray-500 text-center mt-4">
              No credit card required. Manual verification within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SECTION 8: FINAL CTA ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #014c47 100%)` }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-5">Ready to move freight on a modern platform?</h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Free signup. Direct broker relationships. Open marketplace or private networks. Your freight, your rules.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="rounded-full bg-white text-[#02857E] hover:bg-gray-100 font-semibold px-8">
              <Link to="/auth?role=shipper">Sign Up Free <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white text-white hover:bg-white/10 font-semibold px-8">
              <a href="mailto:support@lanerunner.com">Talk to Sales</a>
            </Button>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default ShippersPage;
